// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import {
  type UpdateWorkOrderItemBody,
  type WorkOrderItem,
  type WorkOrderItemId,
  useEvolvePatch,
} from '@evolvemep/api-ts';

import type { AgGridEditableProps } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { pick } from 'helpers/pick';

/**
 * The row fields the item endpoint accepts as patches
 */
const PATCHABLE_FIELDS = [
  'name',
  'quantity',
  'unitOfMeasureId',
  'manufacturerId',
  'manufacturerCatalogNumber',
] as const satisfies readonly (keyof UpdateWorkOrderItemBody & keyof WorkOrderItem)[];

/**
 * Wires up editing a work order item's bill of materials. The grid has two edit paths — the inline editors and the
 * row menu's Edit properties — and both must report the change: the parent item carries `spoolHasManualEdits`,
 * which any quantity change can flip.
 *
 * @param onBomItemPatched Called after every patch
 */
export const useBomItemEditing = (onBomItemPatched?: () => void) => {
  const { apiCall: patchWorkOrderItemApiCall } = useEvolvePatch('shop/workOrder/item/:workOrderItemId');

  /**
   * Spread onto the `AgGridEditable` wrapping the grid, to serve its inline editors. Checked against that
   * component's contract with `satisfies` rather than annotated as it, so `patchCall` keeps its own narrower return
   * type — the contract permits resolving to null to leave a row alone, which this never does.
   */
  const agGridEditableProps = useMemo(
    () =>
      ({
        patchCall: async (body, config) => patchWorkOrderItemApiCall(body, config).finally(() => onBomItemPatched?.()),
        // narrowed to the id so `updateItem` below can reuse it without a whole row on hand
        configMapper: ({ workOrderItemId }: Pick<WorkOrderItem, 'workOrderItemId'>) => ({
          url: `shop/workOrder/item/${workOrderItemId}`,
        }),
        requestConverter: (_, updatedData) => pick(updatedData, PATCHABLE_FIELDS),
      }) satisfies Pick<
        AgGridEditableProps<WorkOrderItem, 'shop/workOrder/item/:workOrderItemId', UpdateWorkOrderItemBody>,
        'patchCall' | 'configMapper' | 'requestConverter'
      >,
    [onBomItemPatched, patchWorkOrderItemApiCall],
  );

  /** Passed to the column definitions, to serve the row menu. */
  const updateItem = useCallback(
    async (workOrderItemId: WorkOrderItemId, body: UpdateWorkOrderItemBody) =>
      agGridEditableProps.patchCall(body, agGridEditableProps.configMapper({ workOrderItemId })),
    [agGridEditableProps],
  );

  return { agGridEditableProps, updateItem };
};
